/**
 * Displays log output, with integrated search.
 */

import {
  edgesToNumberedLines,
  LogViewer,
} from '@wandb/weave/common/components/Log/LogViewer';
import React, {useCallback, useMemo} from 'react';

import {trackFileDownload} from '../../services/analytics/fileDownloadEvents';
import {
  LogsPageLocation,
  trackLogsPage,
} from '../../services/analytics/logsPageEvents';
import {TabWaveLoader} from '../RunDetails/TabWaveLoader';
import {LogLabelsBanner} from './LogLabelsBanner';
import {LogLimitReachedBanner} from './LogLimitReachedBanner';
import {LogLines} from './useRunLogQuery';

interface LogProps {
  logLines: LogLines;
  loading: boolean;
  outputLogFile: string | null;
  totalLines?: number;
  analyticsLocation?: LogsPageLocation;
  // Backward pagination (older logs)
  onLoadPrevious?: () => void;
  hasPrevious?: boolean;
  isLoadingPrevious?: boolean;
}

export const Log = ({
  logLines,
  loading,
  totalLines,
  outputLogFile,
  analyticsLocation,
  onLoadPrevious,
  hasPrevious,
  isLoadingPrevious,
}: LogProps) => {
  const lines = useMemo(() => {
    if (loading || !logLines) {
      return [];
    }
    return edgesToNumberedLines(logLines.edges, totalLines);
  }, [loading, logLines, totalLines]);

  const handleTimestampVisibilityChange = useCallback(
    (isVisible: boolean) => {
      if (analyticsLocation !== undefined) {
        trackLogsPage({
          action: 'Timestamp visibility toggled',
          location: analyticsLocation,
          isTimestampVisible: isVisible,
        });
      }
    },
    [analyticsLocation]
  );

  const handleLabelFiltersApplied = useCallback(
    (selectedCount: number, isSearchActive: boolean) => {
      if (analyticsLocation !== undefined) {
        trackLogsPage({
          action: 'Label filters applied',
          location: analyticsLocation,
          numLabelsSelected: selectedCount,
          isSearchActive,
        });
      }
    },
    [analyticsLocation]
  );

  const handleDownload = useCallback(() => {
    if (analyticsLocation) {
      trackFileDownload({
        fileType: 'log',
        isMultipartLogsFile: false,
        location: analyticsLocation,
      });
    }
  }, [analyticsLocation]);

  return (
    <LogViewer
      hasPrevious={hasPrevious}
      isLoadingPrevious={isLoadingPrevious}
      loading={loading}
      lines={lines}
      totalLines={totalLines}
      outputLogFile={outputLogFile}
      onLoadPrevious={onLoadPrevious}
      onTimestampVisibilityChange={handleTimestampVisibilityChange}
      onLabelFiltersApplied={handleLabelFiltersApplied}
      onLogFileDownload={handleDownload}
      renderLabelsBanner={() => (
        <LogLabelsBanner analyticsLocation={analyticsLocation} />
      )}
      renderLimitReachedBanner={numLinesAvailable => (
        <LogLimitReachedBanner numLinesAvailable={numLinesAvailable} />
      )}
      renderLoading={() => <TabWaveLoader />}
    />
  );
};
